import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  findCollaborator,
  createCollaborator,
  updateCollaborator,
} from "../../../../API/apiCollaborators";
import { CollaboratorInsert } from "../../../../types/Collaborator";
import { validateExistsUsers } from "../../../../API/fetchUsuarios";
import { isValidCPF } from "../../../../utils/DocumentValidators";

export const useCollaboratorSave = (collaboratorId?: string | null) => {
  const navigate = useNavigate();

  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [colaborador, setColaborador] = useState<CollaboratorInsert>({
    nome: "",
    institution_id: 0,
    celular: "",
    telefone: "",
    cargo: "",
    cpf: "",
    email: "",
    email_confirmation: "",
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    if (collaboratorId) {
      consultarColaborador(collaboratorId);
    }
  }, [collaboratorId]);

  const consultarColaborador = async (collaboratorIdValue: string) => {
    setLoading(true);

    try {
      const result = await findCollaborator(collaboratorIdValue);

      if (!result) {
        setMsgError("Nao foi possivel encontrar o colaborador");
        navigate(`/colaboradores`);
      }

      setColaborador({
        nome: result.nome,
        institution_id: result.instituicoes.length > 0 ? result.instituicoes[0].id : 0,
        celular: result.dados_contato.telefone_celular,
        telefone: result.dados_contato.telefone_institucional,
        cargo: result.dados_contato.cargo,
        cpf: result.cpf,
        email: result.email,
        email_confirmation: result.email,
        password: "",
        password_confirmation: "",
      });

      setErrors({});
      setIsEdit(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMsgError("Colaborador nao encontrado");
    }
  };

  const clearFieldError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    setColaborador({ ...colaborador, nome: sanitizedValue });
    clearFieldError("nome");
  };

  const handleInputChange = (value: string | number, nameObject: string, isNumber?: boolean) => {
    setColaborador({ ...colaborador, [nameObject]: isNumber ? Number(value) : value });
    clearFieldError(nameObject);
  };

  const handleChange = (field: keyof CollaboratorInsert, value: any) => {
    setColaborador({ ...colaborador, [field]: value });
    clearFieldError(String(field));
  };

  const handleEmailChange = (email: string, nameObject: string) => {
    setColaborador({ ...colaborador, [nameObject]: email });
    clearFieldError(nameObject);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const hasPasswordInput =
      !!colaborador.password?.trim() || !!colaborador.password_confirmation?.trim();

    if (!colaborador.nome?.trim()) {
      newErrors.nome = "Nome é obrigatório";
    }

    if (!colaborador.institution_id) {
      newErrors.institution_id = "Selecione a instituição";
    }

    if (!colaborador.celular?.trim()) {
      newErrors.celular = "Celular é obrigatório";
    }

    if (!colaborador.cargo?.trim()) {
      newErrors.cargo = "Cargo é obrigatório";
    }

    if (!colaborador.cpf?.trim()) {
      newErrors.cpf = "CPF é obrigatório";
    } else if (!isValidCPF(colaborador.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    if (!colaborador.email?.trim()) {
      newErrors.email = "E-mail é obrigatório";
    }

    if (!colaborador.email_confirmation?.trim()) {
      newErrors.email_confirmation = "E-mail de confirmação é obrigatório";
    }

    if (
      colaborador.email?.trim() &&
      colaborador.email_confirmation?.trim() &&
      colaborador.email !== colaborador.email_confirmation
    ) {
      newErrors.email_confirmation = "Os e-mails informados sao diferentes";
    }

    if (!isEdit || hasPasswordInput) {
      if (!colaborador.password?.trim()) {
        newErrors.password = "Senha é obrigatória";
      } else if (colaborador.password.trim().length < 6) {
        newErrors.password = "A senha deve ter no mínimo 6 caracteres";
      }

      if (!colaborador.password_confirmation?.trim()) {
        newErrors.password_confirmation = "Confirmar senha é obrigatória";
      }
    }

    if (
      colaborador.password?.trim() &&
      colaborador.password_confirmation?.trim() &&
      colaborador.password !== colaborador.password_confirmation
    ) {
      newErrors.password_confirmation = "As senhas informadas sao diferentes";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setMsgError("Preencha os campos obrigatórios");
      return false;
    }

    setMsgError(null);
    return true;
  };

  const validadeExistsColaborador = async () => {
    let dataValidate: any = {
      cpf: colaborador.cpf,
      email: colaborador.email,
    };

    if (collaboratorId) {
      dataValidate = {
        ...dataValidate,
        id: collaboratorId,
      };
    }

    const result = await validateExistsUsers(dataValidate);

    if (result.exists) {
      const messages = result.errors ? result.errors.join(" | ") : "";

      return {
        exists: result.exists,
        message: messages,
      };
    }

    return {
      exists: false,
    };
  };

  const saveColaborador = async () => {
    if (!validateForm()) {
      return;
    }

    setLoadingSave(true);

    try {
      const validate = await validadeExistsColaborador();

      if (validate.exists) {
        setMsgError(validate.message ?? "");

        if (validate.message?.toLowerCase().includes("cpf")) {
          setErrors((prev) => ({ ...prev, cpf: "CPF ja cadastrado" }));
        }

        if (validate.message?.toLowerCase().includes("email")) {
          setErrors((prev) => ({ ...prev, email: "E-mail ja cadastrado" }));
        }

        setLoadingSave(false);
        return false;
      }

      if (collaboratorId) {
        const payloadUpdate: Partial<CollaboratorInsert> = { ...colaborador };
        const shouldSendPassword =
          !!colaborador.password?.trim() || !!colaborador.password_confirmation?.trim();

        if (!shouldSendPassword) {
          delete payloadUpdate.password;
          delete payloadUpdate.password_confirmation;
        }

        await updateCollaborator(collaboratorId, payloadUpdate);
        setMsgSuccess("Colaborador atualizado com sucesso!");
      } else {
        await createCollaborator(colaborador);
        setMsgSuccess("Colaborador cadastrado com sucesso!");
      }

      setLoadingSave(false);
      navigate(`/colaboradores`);
    } catch (error) {
      setLoadingSave(false);
      setMsgError("Nao foi possivel cadastrar o colaborador");
      return false;
    }
  };

  return {
    msgError,
    msgSuccess,
    errors,
    isEdit,
    loading,
    loadingSave,
    colaborador,
    setMsgError,
    setMsgSuccess,
    setColaborador,
    handleNameChange,
    handleInputChange,
    handleChange,
    handleEmailChange,
    saveColaborador,
  };
};
